/* eslint-disable no-restricted-syntax */
import { Logtail } from "@logtail/browser";
import { env } from "@/env";
import { logger as posthogLogger } from "@/lib/providers/posthog/logger";

/**
 * Better Stack (Logtail) telemetry client for remote logging. Only initializes if
 * VITE_BETTER_STACK_SOURCE_TOKEN is provided. Requires BOTH source token AND ingesting host for
 * HTTP sources.
 *
 * @see https://betterstack.com/docs/logs/javascript/browser/
 */

// Initialize Logtail client only if token is provided
let logtailClient: Logtail | null = null;

if (env.VITE_BETTER_STACK_SOURCE_TOKEN) {
  console.log(
    "[Better Stack] Initializing with token:",
    env.VITE_BETTER_STACK_SOURCE_TOKEN.substring(0, 8) + "...",
  );

  if (!env.VITE_BETTER_STACK_INGESTING_HOST) {
    console.warn(
      "[Better Stack] VITE_BETTER_STACK_INGESTING_HOST is not set. Logs may not be sent.",
    );
  }

  logtailClient = new Logtail(env.VITE_BETTER_STACK_SOURCE_TOKEN, {
    // Use custom ingesting host (required for HTTP sources in Better Stack)
    endpoint: env.VITE_BETTER_STACK_INGESTING_HOST
      ? `https://${env.VITE_BETTER_STACK_INGESTING_HOST}`
      : undefined,
  });

  console.log(
    "[Better Stack] Client initialized successfully" +
      (env.VITE_BETTER_STACK_INGESTING_HOST
        ? ` with custom endpoint: https://${env.VITE_BETTER_STACK_INGESTING_HOST}`
        : ""),
  );
} else {
  console.log("[Better Stack] No source token provided - remote logging disabled");
}

/** Sanitize sensitive data from objects before logging */
function sanitizeData(data: unknown): unknown {
  if (!data || typeof data !== "object") {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }

  const sanitized: Record<string, unknown> = {};
  const sensitiveKeys = [
    "password",
    "token",
    "secret",
    "apiKey",
    "api_key",
    "authorization",
    "auth",
    "cookie",
    "session",
  ];

  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
      sanitized[key] = "[REDACTED]";
    } else if (typeof value === "object" && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/** Sanitize URLs by removing sensitive query parameters */
function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const sensitiveParams = ["token", "api_key", "apiKey", "secret", "password", "auth"];

    sensitiveParams.forEach((param) => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, "[REDACTED]");
      }
    });

    return urlObj.toString();
  } catch {
    return url;
  }
}

/** User context for telemetry logs */
interface UserContext {
  userId?: string;
  organizationId?: string;
  userEmail?: string;
  environment?: string;
  appSlug?: string;
  appTarget?: string;
  userAgent?: string;
}

// Initialize with build-time environment context
let userContext: UserContext = {
  appSlug: env.VITE_ORGANIZATION_SLUG,
  appTarget: env.VITE_TARGET,
  environment: env.MODE,
  userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
};

/** Better Stack telemetry logger with automatic data sanitization */
export class TelemetryLogger {
  /** Set user context for all future logs */
  static setUserContext(context: UserContext) {
    userContext = { ...userContext, ...context };
  }

  /** Clear user context (e.g., on logout) */
  static clearUserContext() {
    userContext = {};
  }

  /** Log debug message */
  static debug(message: string, context?: Record<string, unknown>) {
    this.log("debug", message, context);
    posthogLogger.debug(message, context);
  }

  /** Log info message */
  static info(message: string, context?: Record<string, unknown>) {
    this.log("info", message, context);
    posthogLogger.info(message, context);
  }

  /** Log warning message */
  static warn(message: string, context?: Record<string, unknown>) {
    this.log("warn", message, context);
    posthogLogger.warn(message, context);
  }

  /** Log error message */
  static error(message: string, error?: Error | unknown, context?: Record<string, unknown>) {
    const errorContext =
      error instanceof Error
        ? {
            errorMessage: error.message,
            errorStack: error.stack,
            errorName: error.name,
          }
        : { error };

    const fullContext = { ...errorContext, ...context };
    this.log("error", message, fullContext);
    posthogLogger.error(message, fullContext);
  }

  /** Log network request (API calls) */
  static logNetworkRequest({
    method,
    url,
    status,
    duration,
    error,
    requestBody,
    responseBody,
    requestHeaders,
  }: {
    method: string;
    url: string;
    status?: number;
    duration?: number;
    error?: Error | string | unknown;
    requestBody?: unknown;
    responseBody?: unknown;
    requestHeaders?: Record<string, string>;
  }) {
    const sanitizedUrl = sanitizeUrl(url);
    const level = status && status >= 400 ? "error" : "info";

    // Create a descriptive message like "Network request error: GET https://..."
    const message =
      status && status >= 400
        ? `Network request error: ${method} ${sanitizedUrl}`
        : `Network request: ${method} ${sanitizedUrl}`;

    this.log(level, message, {
      url: sanitizedUrl,
      method,
      status,
      duration,
      error: sanitizeData(error),
      requestBody: sanitizeData(requestBody),
      responseBody: sanitizeData(responseBody),
      requestHeaders: requestHeaders ? sanitizeData(requestHeaders) : undefined,
      type: "network_request",
    });
  }

  /** Internal log method */
  private static log(level: string, message: string, context?: Record<string, unknown>) {
    if (!logtailClient) {
      return; // Remote logging disabled
    }

    const sanitizedContext = context ? (sanitizeData(context) as Record<string, unknown>) : {};

    logtailClient.log(message, level, {
      ...sanitizedContext,
      ...userContext,
    });
  }

  /** Flush all pending logs (call before page unload) */
  static async flush(): Promise<void> {
    if (!logtailClient) {
      return;
    }

    try {
      await logtailClient.flush();
    } catch (error) {
      console.error("[Better Stack] Failed to flush logs:", error);
    }
  }
}
